import { RestrictToVerticalAxis } from "@dnd-kit/abstract/modifiers";
import { useDraggable } from "@dnd-kit/react";
import { Button } from "@repo/ui/components/ui/button";
import { cn } from "@repo/ui/lib/utils";
import { GripVertical, Link2, Trash2 } from "lucide-react";
import { type ProcedureTimelineGesture, useEditableTimeline } from "./editable";
import { SplitProcedureButton } from "./split-procedure-button";

/**
 * A procedure row's drag source. `surface` keeps ids unique where two surfaces
 * draw the same procedure under one `EditableTimelineProvider` — dnd-kit keys
 * its registry by id, so a shared one would leave only the last row draggable.
 */
export function useTimelineProcedureDrag({
  procedureId,
  stepId,
  disabled,
  surface = "grid",
}: {
  procedureId: string;
  stepId: string;
  disabled: boolean;
  surface?: string;
}) {
  return useDraggable<ProcedureTimelineGesture>({
    id: `procedure:${surface}:${procedureId}`,
    type: "timeline-procedure",
    modifiers: [RestrictToVerticalAxis],
    data: { kind: "procedure", procedureId, stepId },
    disabled,
  });
}

export function TimelineProcedureRow({
  procedure,
  selected,
  onSelect,
  stepId,
  editable,
  onRemove,
  onSplit,
}: {
  procedure: {
    id: string;
    title: string;
    completed?: boolean;
    statusLabel?: string;
    examNames?: readonly string[];
  };
  selected?: boolean;
  onSelect?: () => void;
  stepId: string;
  editable: boolean;
  onRemove?: (orderId: string) => void;
  onSplit?: (orderId: string) => void;
}) {
  const {
    meta: { readOnly, editable: timelineEditable },
  } = useEditableTimeline();
  const canEdit = editable && timelineEditable;
  const name = procedure.title;
  const completed = procedure.completed ?? false;
  const drag = useTimelineProcedureDrag({ procedureId: procedure.id, stepId, disabled: !canEdit || completed });
  return (
    <div
      ref={readOnly ? undefined : drag.ref}
      className={cn(
        "flex min-w-0 items-center gap-1 px-1 py-1 text-[11px]",
        !readOnly && selected && "rounded bg-primary/10 ring-1 ring-inset ring-primary",
      )}
    >
      {!readOnly && (
        <Button
          ref={drag.handleRef}
          type="button"
          variant="ghost"
          size="icon-xs"
          disabled={!canEdit || completed}
          className="pointer-events-auto size-4 shrink-0 touch-none cursor-grab text-muted-foreground active:cursor-grabbing"
          aria-label={`Move ${name} to another step`}
          title="Drag to another step"
        >
          <GripVertical className="size-3" />
        </Button>
      )}
      {!readOnly && onSelect ? (
        <button
          type="button"
          className="min-w-0 flex-1 truncate text-left"
          title={name}
          aria-pressed={selected}
          onClick={onSelect}
        >
          {name}
        </button>
      ) : (
        <span className="min-w-0 flex-1 truncate" title={name}>
          {name}
        </span>
      )}
      {!!procedure.examNames?.length && (
        <span
          role="img"
          className="flex max-w-32 shrink items-center gap-0.5 text-muted-foreground"
          title={`Required by ${procedure.examNames.join(", ")}`}
          aria-label={`Required by ${procedure.examNames.join(", ")}`}
        >
          <Link2 aria-hidden="true" className="size-3 shrink-0" />
          <span className="truncate">{procedure.examNames.join(", ")}</span>
        </span>
      )}
      {!readOnly && onSplit && !completed && (
        <SplitProcedureButton
          procedureName={name}
          disabled={!canEdit}
          className="pointer-events-auto"
          onClick={() => onSplit(procedure.id)}
        />
      )}

      {(procedure.statusLabel || completed) && (
        <span className="text-muted-foreground">{procedure.statusLabel ?? "Done"}</span>
      )}
      {!readOnly && onRemove && !completed && (
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          disabled={!canEdit}
          className="pointer-events-auto size-5 shrink-0 text-destructive"
          aria-label={`Remove ${name}`}
          onClick={() => onRemove(procedure.id)}
        >
          <Trash2 className="size-3" />
        </Button>
      )}
    </div>
  );
}
